import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Platform, Pressable, Text, View } from 'react-native'
import { reportError } from '@/hooks/useAnalytics'

type AppErrorBoundaryProps = {
  children: ReactNode
}

type AppErrorBoundaryState = {
  hasError: boolean
}

export default class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = {
    hasError: false,
  }

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AppErrorBoundary] Unhandled render error', error, info.componentStack)

    if (Platform.OS === 'web') {
      reportError(error, {
        $exception_type: 'React',
        component_stack: info.componentStack,
      })
    }
  }

  private handleReload = () => {
    if (Platform.OS !== 'web') return

    const webGlobal = globalThis as typeof globalThis & {
      location?: { reload?: () => void }
    }
    webGlobal.location?.reload?.()
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: 24,
          backgroundColor: '#ffffff',
          gap: 12,
        }}
      >
        <Text
          style={{
            fontSize: 20,
            lineHeight: 28,
            fontWeight: '700',
            color: '#111827',
            textAlign: 'center',
          }}
        >
          Something went wrong.
        </Text>
        <Text
          style={{
            fontSize: 14,
            lineHeight: 20,
            color: '#4b5563',
            textAlign: 'center',
            maxWidth: 460,
          }}
        >
          The app hit an unexpected error. Try reloading. If this keeps happening, report it to
          support.
        </Text>
        {Platform.OS === 'web' ? (
          <Pressable
            onPress={this.handleReload}
            style={{
              marginTop: 8,
              borderRadius: 10,
              paddingHorizontal: 16,
              paddingVertical: 10,
              backgroundColor: '#0f766e',
            }}
          >
            <Text style={{ color: '#ffffff', fontWeight: '700' }}>Reload</Text>
          </Pressable>
        ) : null}
      </View>
    )
  }
}
